import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Stack, Typography } from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import SportsEsportsOutlinedIcon from "@mui/icons-material/SportsEsportsOutlined";
import { useGetProgressQuery } from "@store/gameApi";
import { RootState } from "@store/store";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import DomainRadarMiniChart from "@components/Home/Games/GamesCTATile/DomainRadarMiniChart";
import LastSessionSummaryLine from "@components/Home/Games/GamesCTATile/LastSessionSummaryLine";
import WeeklyActivityBar from "@components/Home/Games/GamesCTATile/WeeklyActivityBar";

export interface GamesCTATileProps {
  /** Base path for navigation (e.g. "" for caregiver, "portal" for patient) */
  basePath?: string;
}

const GamesCTATile: FC<GamesCTATileProps> = ({ basePath = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: progress } = useGetProgressQuery(
    { patientId: selectedPatientId! },
    { skip: !selectedPatientId },
  );
  const prefix = basePath ? `/${basePath}` : "";

  const handleStartSession = () => {
    navigate(`${prefix}/games/guided`);
  };

  const handlePlayAGame = () => {
    navigate(`${prefix}/games`);
  };

  const handleRadarTap = () => {
    navigate(`${prefix}/games`);
  };

  return (
    <DashboardTile title={t("games.cta.title")}>
      {progress?.streakDay != null && progress.streakDay > 0 && (
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
          <LocalFireDepartmentIcon sx={{ color: "warning.main", fontSize: 20 }} />
          <Typography variant="body2">
            {t("games.cta.streak", { count: progress.streakDay })}
          </Typography>
        </Stack>
      )}
      <Stack spacing={2}>
        <Stack spacing={0.25}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleStartSession}
          >
            <Stack spacing={0.25}>
              {t("games.cta.startSession")}
              <Typography variant="body2" sx={{ px: 0.5, opacity: 0.95 }}>
                {t("games.cta.startSessionSubtitle")}
              </Typography>
            </Stack>
          </Button>
        </Stack>
        <Stack spacing={0.25}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<SportsEsportsOutlinedIcon />}
            onClick={handlePlayAGame}
          >
            {t("games.cta.playAGame")}
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2} sx={{ mt: 3 }}>
        <DomainRadarMiniChart progress={progress ?? null} onTap={handleRadarTap} />
        <LastSessionSummaryLine progress={progress ?? null} />
        <WeeklyActivityBar progress={progress ?? null} />
      </Stack>
    </DashboardTile>
  );
};

export default GamesCTATile;
